import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { toPng } from "html-to-image";
import { BRAND, POSITION } from "../../config/theme";
import { useCountUp } from "../../helpers/useCountUp";
import type { WrappedMetrics, WrappedSlide, PlatformMetrics } from "../../helpers/wrappedMetrics";

// Committed dark cinematic palette (the Wrapped is its own visual world, distinct
// from the app's light/dark dashboards). Platform tones are rosterOS-neutral
// category colors — NOT the platforms' brand colors, which would clash with Signal.
const C = {
  text: "#f2f3f5",
  dim: "#a8afba",
  caption: "#818893",
  green: BRAND.green,
  greenOnDark: BRAND.greenOnDark,
  good: "#3fce85",
  hairline: "rgba(255,255,255,.10)",
  ud: "#d9a441",
  dk: "#6c7bd9",
  cin: "#FB4F14",
};
const POS_HEX: Record<string, string> = { QB: POSITION.QB, RB: POSITION.RB, WR: POSITION.WR, TE: POSITION.TE };
const SLIDE_MS = 5400;

function platformTone(provider: string): string {
  return provider === "DraftKings" ? C.dk : C.ud;
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return;
    setReduced(mq.matches);
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduced;
}

// ── small presentational atoms ────────────────────────────────────

function Reveal({ children, delay = 0, reduced }: { children: React.ReactNode; delay?: number; reduced: boolean }) {
  return (
    <Box
      sx={{
        opacity: reduced ? 1 : 0,
        animation: reduced ? "none" : `wrapReveal .55s cubic-bezier(0.4,0,0.2,1) forwards`,
        animationDelay: `${delay}ms`,
        "@keyframes wrapReveal": {
          from: { opacity: 0, transform: "translateY(16px)" },
          to: { opacity: 1, transform: "none" },
        },
      }}
    >
      {children}
    </Box>
  );
}

function CountNumber({ value, decimals = 0, reduced }: { value: number; decimals?: number; reduced: boolean }) {
  const v = useCountUp(value, 900, reduced);
  return <>{decimals ? v.toFixed(decimals) : Math.round(v).toLocaleString("en-US")}</>;
}

function PlatformChip({ provider }: { provider: string }) {
  const tone = platformTone(provider);
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", gap: "6px",
      fontSize: 11, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase",
      padding: "4px 9px", borderRadius: "7px",
      color: tone, background: `${tone}28`, boxShadow: `inset 0 0 0 1px ${tone}66`,
    }}>
      <Box component="span" sx={{ width: 8, height: 8, borderRadius: "2px", background: tone }} />
      {provider}
    </Box>
  );
}

function Bar({ pct, color, delay = 200, reduced }: { pct: number; color: string; delay?: number; reduced: boolean }) {
  return (
    <Box sx={{ height: 12, borderRadius: "7px", background: "rgba(255,255,255,.09)", overflow: "hidden", mt: 1 }}>
      <Box sx={{
        height: "100%", borderRadius: "7px", background: color,
        width: reduced ? `${pct}%` : 0,
        animation: reduced ? "none" : `wrapBar 1s cubic-bezier(0.4,0,0.2,1) forwards`,
        animationDelay: `${delay}ms`,
        "@keyframes wrapBar": { to: { width: `${pct}%` } },
      }} />
    </Box>
  );
}

const eyebrowSx = { fontSize: 12, fontWeight: 700, letterSpacing: ".16em", textTransform: "uppercase", color: C.greenOnDark, mb: 2.25 } as const;
const subSx = { color: C.dim, fontSize: 14.5, lineHeight: 1.55, mt: 2, maxWidth: "30ch" } as const;

// ── slide renderer ────────────────────────────────────────────────

function bgFor(type: WrappedSlide["type"]): string {
  switch (type) {
    case "intro":
    case "markets":
    case "personality":
      return `radial-gradient(120% 90% at 30% 8%, #244a1e 0%, rgba(22,48,20,0) 40%), linear-gradient(180deg, #1f3a1a 0%, #12240f 100%)`;
    case "player":
      return `radial-gradient(120% 80% at 78% 12%, rgba(230,81,0,.30) 0%, rgba(230,81,0,0) 46%), linear-gradient(180deg, #16130f 0%, #0a0a08 100%)`;
    case "stack":
      return `radial-gradient(120% 80% at 50% 14%, rgba(251,79,20,.24) 0%, rgba(251,79,20,0) 50%), linear-gradient(180deg, #17110c 0%, #0a0806 100%)`;
    case "steal":
      return `radial-gradient(120% 80% at 72% 14%, rgba(63,206,133,.22) 0%, rgba(63,206,133,0) 48%), linear-gradient(180deg, #0c1a12 0%, #060f0a 100%)`;
    case "platformSplit":
      return `radial-gradient(100% 70% at 20% 10%, rgba(217,164,65,.16) 0%, rgba(217,164,65,0) 42%), radial-gradient(100% 70% at 88% 16%, rgba(108,123,217,.18) 0%, rgba(108,123,217,0) 44%), linear-gradient(180deg, #101511 0%, #070c09 100%)`;
    default:
      return `linear-gradient(180deg, #0e2410 0%, #06120a 100%)`;
  }
}

function SlideBody({ slide, m, reduced }: { slide: WrappedSlide; m: WrappedMetrics; reduced: boolean }) {
  const metricSx = { fontWeight: 800, letterSpacing: "-.03em", lineHeight: .92, fontVariantNumeric: "tabular-nums", fontSize: "clamp(72px, 20vw, 104px)" } as const;

  switch (slide.type) {
    case "intro":
      return (
        <>
          <Reveal reduced={reduced}><Typography sx={eyebrowSx}>rosterOS · 2026</Typography></Reveal>
          <Reveal delay={80} reduced={reduced}>
            <Typography sx={{ fontSize: 34, fontWeight: 800, lineHeight: 1.15, letterSpacing: "-.01em" }}>
              {m.hasMultiplePlatforms ? <>Two platforms.<br />One season.</> : <>Your draft season,<br />wrapped.</>}
            </Typography>
          </Reveal>
          {m.hasMultiplePlatforms && (
            <Reveal delay={160} reduced={reduced}>
              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                {m.platforms.map(p => <PlatformChip key={p.provider} provider={p.provider} />)}
              </Box>
            </Reveal>
          )}
          {m.seasonWindow && (
            <Reveal delay={240} reduced={reduced}>
              <Typography sx={subSx}>{m.seasonWindow.startLabel} → {m.seasonWindow.endLabel} · from your first pick to kickoff.</Typography>
            </Reveal>
          )}
        </>
      );

    case "drafts":
      return (
        <>
          <Reveal reduced={reduced}><Typography sx={eyebrowSx}>You entered the arena</Typography></Reveal>
          <Reveal delay={80} reduced={reduced}><Typography sx={metricSx}><CountNumber value={m.combined.drafts} reduced={reduced} /></Typography></Reveal>
          <Reveal delay={140} reduced={reduced}><Typography sx={{ fontSize: 15, color: C.dim, fontWeight: 600, mt: 0.5 }}>drafts this summer</Typography></Reveal>
        </>
      );

    case "platformSplit": {
      const total = m.combined.drafts || 1;
      return (
        <>
          <Reveal reduced={reduced}><Typography sx={eyebrowSx}>Where you drafted</Typography></Reveal>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
            {m.platforms.map((p, i) => (
              <Reveal key={p.provider} delay={80 + i * 90} reduced={reduced}>
                <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 1 }}>
                  <PlatformChip provider={p.provider} />
                  <Typography sx={{ fontSize: 30, fontWeight: 800, fontVariantNumeric: "tabular-nums", letterSpacing: "-.02em" }}>
                    <CountNumber value={p.drafts} reduced={reduced} />
                    <Typography component="span" sx={{ fontSize: 14, color: C.dim, fontWeight: 600, ml: 0.75 }}>drafts · ${p.invested.toLocaleString()}</Typography>
                  </Typography>
                </Box>
                <Bar pct={Math.round((p.drafts / total) * 100)} color={platformTone(p.provider)} delay={140 + i * 90} reduced={reduced} />
              </Reveal>
            ))}
          </Box>
          <Reveal delay={360} reduced={reduced}>
            <Typography sx={{ textAlign: "center", mt: 2, fontSize: 13, color: C.caption, fontWeight: 600 }}>
              {m.combined.drafts} drafts total · ${m.combined.invested.toLocaleString()} on the board
            </Typography>
          </Reveal>
        </>
      );
    }

    case "players":
      return (
        <>
          <Reveal reduced={reduced}><Typography sx={eyebrowSx}>{m.hasMultiplePlatforms ? "Across both platforms you rostered" : "You rostered"}</Typography></Reveal>
          <Reveal delay={80} reduced={reduced}><Typography sx={metricSx}><CountNumber value={m.combined.uniquePlayers} reduced={reduced} /></Typography></Reveal>
          <Reveal delay={140} reduced={reduced}><Typography sx={{ fontSize: 15, color: C.dim, fontWeight: 600, mt: 0.5 }}>different players</Typography></Reveal>
        </>
      );

    case "player": {
      const tp = m.topPlayer!;
      const posColor = POS_HEX[tp.position] || C.greenOnDark;
      return (
        <>
          <Reveal reduced={reduced}><Typography sx={eyebrowSx}>Your most-drafted player</Typography></Reveal>
          <Reveal delay={80} reduced={reduced}><Typography sx={{ fontSize: 38, fontWeight: 800, letterSpacing: "-.02em", lineHeight: 1.05, color: posColor }}>{tp.name}</Typography></Reveal>
          <Reveal delay={140} reduced={reduced}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, mt: 1.25 }}>
              <Box component="span" sx={{ fontSize: 11, fontWeight: 800, px: 1, py: "3px", borderRadius: "6px", color: "#fff", background: posColor }}>{tp.position}</Box>
              <Typography component="span" sx={{ fontSize: 13.5, color: C.dim, fontWeight: 600 }}>{tp.team || "on your teams"}</Typography>
            </Box>
          </Reveal>
          {m.hasMultiplePlatforms ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75, mt: 3 }}>
              {m.platforms.map((p, i) => (
                <Reveal key={p.provider} delay={200 + i * 90} reduced={reduced}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                    <PlatformChip provider={p.provider} />
                    <Typography sx={{ fontSize: 22, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                      <CountNumber value={Math.round(tp.byProvider[p.provider] || 0)} reduced={reduced} /><Box component="span" sx={{ fontSize: ".6em", color: C.dim }}>%</Box>
                    </Typography>
                  </Box>
                  <Bar pct={Math.round(tp.byProvider[p.provider] || 0)} color={platformTone(p.provider)} delay={260 + i * 90} reduced={reduced} />
                </Reveal>
              ))}
            </Box>
          ) : (
            <Reveal delay={200} reduced={reduced}>
              <Box sx={{ mt: 3 }}>
                <Typography sx={{ fontSize: 58, fontWeight: 800, letterSpacing: "-.02em", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
                  <CountNumber value={Math.round(tp.exposurePct)} reduced={reduced} /><Box component="span" sx={{ fontSize: ".42em", color: C.dim }}>%</Box>
                </Typography>
                <Bar pct={Math.round(tp.exposurePct)} color={posColor} reduced={reduced} />
                <Typography sx={{ fontSize: 11.5, color: C.caption, mt: 1 }}>of your teams · 8.3% = one in twelve</Typography>
              </Box>
            </Reveal>
          )}
        </>
      );
    }

    case "posLean":
      return (
        <>
          <Reveal reduced={reduced}><Typography sx={eyebrowSx}>How you built{m.hasMultiplePlatforms ? " · both platforms" : ""}</Typography></Reveal>
          <Reveal delay={80} reduced={reduced}>
            <Typography sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1.18 }}>You built through the {m.posLean[0].pos} room.</Typography>
          </Reveal>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75, mt: 3 }}>
            {m.posLean.map((p, i) => (
              <Reveal key={p.pos} delay={140 + i * 70} reduced={reduced}>
                <Box sx={{ display: "grid", gridTemplateColumns: "34px 1fr 44px", alignItems: "center", gap: 1.5 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: POS_HEX[p.pos] || C.dim }}>{p.pos}</Typography>
                  <Box><Bar pct={p.pct} color={POS_HEX[p.pos] || C.greenOnDark} delay={180 + i * 70} reduced={reduced} /></Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.dim, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{p.pct}%</Typography>
                </Box>
              </Reveal>
            ))}
          </Box>
        </>
      );

    case "markets":
      return (
        <>
          <Reveal reduced={reduced}><Typography sx={eyebrowSx}>Your edge, by market</Typography></Reveal>
          <Reveal delay={80} reduced={reduced}>
            <Typography sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1.2 }}>Two boards. Two ADPs.<br />Value on both.</Typography>
          </Reveal>
          <Box sx={{ display: "flex", gap: 1.75, mt: 3 }}>
            {m.platforms.map((p, i) => (
              <Reveal key={p.provider} delay={140 + i * 90} reduced={reduced}>
                <Box sx={{ flex: 1, background: "rgba(255,255,255,.04)", border: `1px solid ${C.hairline}`, borderRadius: "16px", p: 2 }}>
                  <PlatformChip provider={p.provider} />
                  <Typography sx={{ fontSize: 40, fontWeight: 800, color: C.good, fontVariantNumeric: "tabular-nums", letterSpacing: "-.02em", lineHeight: 1, mt: 1.5 }}>
                    {p.avgClv !== null && p.avgClv >= 0 ? "+" : ""}<CountNumber value={p.avgClv ?? 0} decimals={1} reduced={reduced} />
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: C.caption, mt: 1, fontWeight: 600, lineHeight: 1.4 }}>avg picks of value<br />vs {p.provider} ADP</Typography>
                </Box>
              </Reveal>
            ))}
          </Box>
          <Reveal delay={340} reduced={reduced}><Typography sx={subSx}>Each pick is scored against its own platform's market — never mixed.</Typography></Reveal>
        </>
      );

    case "steal": {
      const s = m.biggestSteal!;
      const posColor = POS_HEX[s.position] || C.greenOnDark;
      return (
        <>
          <Reveal reduced={reduced}><Typography sx={eyebrowSx}>Your biggest steal</Typography></Reveal>
          <Reveal delay={80} reduced={reduced}><Typography sx={{ fontSize: 38, fontWeight: 800, letterSpacing: "-.02em", lineHeight: 1.05 }}>{s.name}</Typography></Reveal>
          <Reveal delay={140} reduced={reduced}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, mt: 1.25 }}>
              <Box component="span" sx={{ fontSize: 11, fontWeight: 800, px: 1, py: "3px", borderRadius: "6px", color: "#fff", background: posColor }}>{s.position}</Box>
              <PlatformChip provider={s.provider} />
            </Box>
          </Reveal>
          <Reveal delay={200} reduced={reduced}>
            <Typography sx={{ fontSize: "clamp(84px,22vw,112px)", fontWeight: 800, color: C.good, letterSpacing: "-.03em", lineHeight: .9, fontVariantNumeric: "tabular-nums", mt: 2 }}>
              +<CountNumber value={Math.round(s.clv)} reduced={reduced} /><Box component="span" sx={{ fontSize: ".34em", color: C.dim, fontWeight: 700 }}> picks</Box>
            </Typography>
          </Reveal>
          <Reveal delay={260} reduced={reduced}><Typography sx={subSx}>You took him at {s.pick} — {s.provider}'s board had him at {s.marketAdp}.</Typography></Reveal>
        </>
      );
    }

    case "stack": {
      const st = m.signatureStack!;
      return (
        <>
          <Reveal reduced={reduced}><Typography sx={eyebrowSx}>Your signature stack</Typography></Reveal>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mt: 1 }}>
            {[st.a, st.b].map((nm, i) => (
              <Reveal key={nm} delay={80 + i * 80} reduced={reduced}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, background: "rgba(255,255,255,.05)", border: `1px solid ${C.hairline}`, borderRadius: "14px", p: "14px 16px" }}>
                  <Box sx={{ width: 6, alignSelf: "stretch", borderRadius: "4px", background: C.cin }} />
                  <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{nm}</Typography>
                </Box>
              </Reveal>
            ))}
          </Box>
          <Reveal delay={240} reduced={reduced}>
            <Typography sx={{ mt: 2.5, fontSize: 15, color: C.dim, fontWeight: 600 }}>
              paired <Box component="span" sx={{ color: C.text, fontSize: 40, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}><CountNumber value={st.count} reduced={reduced} /></Box> times
            </Typography>
          </Reveal>
        </>
      );
    }

    case "personality":
      return (
        <>
          <Reveal reduced={reduced}><Typography sx={eyebrowSx}>{m.hasMultiplePlatforms ? "You drafted differently on each site" : "Your drafting personality"}</Typography></Reveal>
          {m.hasMultiplePlatforms ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
              {m.platforms.filter(p => p.personality).map((p, i) => (
                <Reveal key={p.provider} delay={80 + i * 90} reduced={reduced}>
                  <Box sx={{ background: "rgba(255,255,255,.04)", border: `1px solid ${C.hairline}`, borderLeft: `4px solid ${platformTone(p.provider)}`, borderRadius: "16px", p: "16px 18px" }}>
                    <PlatformChip provider={p.provider} />
                    <Typography sx={{ fontSize: 24, fontWeight: 800, letterSpacing: "-.01em", mt: 0.75 }}>{p.personality!.primary}</Typography>
                  </Box>
                </Reveal>
              ))}
            </Box>
          ) : (
            <Reveal delay={80} reduced={reduced}>
              <Typography sx={{ fontSize: 46, fontWeight: 800, letterSpacing: "-.02em", lineHeight: 1.02 }}>{m.platforms[0]?.personality?.primary ?? "Balanced"}</Typography>
            </Reveal>
          )}
        </>
      );

    case "temporal": {
      const t = m.temporal!;
      const peak = Math.max(...t.months.map(x => x.count), 1);
      return (
        <>
          <Reveal reduced={reduced}><Typography sx={eyebrowSx}>Your busiest stretch</Typography></Reveal>
          <Reveal delay={80} reduced={reduced}><Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1.15 }}>{t.peakMonthLabel.split(" ")[0]} was your month.</Typography></Reveal>
          <Reveal delay={140} reduced={reduced}><Typography sx={{ fontSize: 15, color: C.dim, fontWeight: 600, mt: 0.5 }}><CountNumber value={t.peakCount} reduced={reduced} /> drafts{m.hasMultiplePlatforms ? " across both platforms" : ""}.</Typography></Reveal>
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1.25, height: 132, mt: 3 }}>
            {t.months.map((mo, i) => (
              <Box key={mo.label + i} sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 1, height: "100%", justifyContent: "flex-end" }}>
                <Box sx={{
                  width: "100%", maxWidth: 30, borderRadius: "6px 6px 0 0",
                  background: mo.count === peak ? C.greenOnDark : "#356b2b",
                  height: reduced ? `${Math.round((mo.count / peak) * 116) + 8}px` : 0,
                  animation: reduced ? "none" : "wrapStem .9s cubic-bezier(0.4,0,0.2,1) forwards",
                  animationDelay: `${180 + i * 60}ms`,
                  "@keyframes wrapStem": { to: { height: `${Math.round((mo.count / peak) * 116) + 8}px` } },
                }} />
                <Typography sx={{ fontSize: 11, color: C.caption, fontWeight: 600 }}>{mo.label.split(" ")[0]}</Typography>
              </Box>
            ))}
          </Box>
        </>
      );
    }

    case "summary":
      return null; // rendered by SummaryCard overlay
    default:
      return null;
  }
}

// ── summary / share card ──────────────────────────────────────────

function SummaryCard({ m, cardRef }: { m: WrappedMetrics; cardRef: React.RefObject<HTMLDivElement | null> }) {
  const ud = m.platforms.find(p => p.provider === "Underdog");
  const dk = m.platforms.find(p => p.provider === "DraftKings");
  const cell = (k: string, v: React.ReactNode, good = false) => (
    <Box>
      <Typography sx={{ fontSize: 10, letterSpacing: ".11em", textTransform: "uppercase", color: C.caption, fontWeight: 700 }}>{k}</Typography>
      <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: "-.01em", mt: 0.4, fontVariantNumeric: "tabular-nums", color: good ? C.good : C.text }}>{v}</Typography>
    </Box>
  );
  const fmtClv = (p?: PlatformMetrics) => (p?.avgClv != null ? `${p.avgClv >= 0 ? "+" : ""}${p.avgClv.toFixed(1)}` : "—");
  return (
    <Box ref={cardRef} sx={{
      position: "absolute", inset: 0, p: "40px 30px 24px", display: "flex", flexDirection: "column",
      background: `radial-gradient(120% 60% at 50% 0%, #244a1e 0%, rgba(36,74,30,0) 45%), linear-gradient(180deg, #163014 0%, #081007 100%)`,
    }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box sx={{ width: 26, height: 26, borderRadius: "7px", background: `linear-gradient(150deg, #356b2b, ${C.green})`, display: "grid", placeItems: "center" }}>
            <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#eaf5e6" }}>r</Typography>
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 800, letterSpacing: ".02em", color: C.text }}>rosterOS</Typography>
        </Box>
        <Typography sx={{ fontSize: 12, color: C.caption, fontWeight: 700, letterSpacing: ".1em" }}>2026</Typography>
      </Box>
      <Box sx={{ mt: 2.25 }}>
        <Typography sx={{ fontSize: 40, fontWeight: 800, letterSpacing: "-.02em", lineHeight: .98, color: C.text }}>SEASON</Typography>
        <Typography sx={{ fontSize: 40, fontWeight: 800, letterSpacing: "-.02em", lineHeight: .98, color: C.greenOnDark }}>WRAPPED</Typography>
      </Box>
      {m.hasMultiplePlatforms && (
        <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>{m.platforms.map(p => <PlatformChip key={p.provider} provider={p.provider} />)}</Box>
      )}
      <Box sx={{ height: "2px", background: `linear-gradient(90deg, ${C.greenOnDark}, transparent)`, my: 1.75 }} />
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 18px", flex: 1, alignContent: "center" }}>
        {cell("Drafts", m.hasMultiplePlatforms
          ? <>{m.combined.drafts} <Box component="span" sx={{ fontSize: 12, color: C.dim, fontWeight: 600 }}>{ud?.drafts ?? 0} UD · {dk?.drafts ?? 0} DK</Box></>
          : m.combined.drafts)}
        {cell("Invested", `$${m.combined.invested.toLocaleString()}`)}
        {m.topPlayer && cell("Top exposure", <Box component="span" sx={{ fontSize: 17 }}>{m.topPlayer.name} <Box component="span" sx={{ fontSize: 12, color: C.dim, fontWeight: 600 }}>{Math.round(m.topPlayer.exposurePct)}%</Box></Box>)}
        {m.hasMultiplePlatforms
          ? cell("Edge · UD / DK", <>{fmtClv(ud)} <Box component="span" sx={{ fontSize: 13, color: C.good }}>/ {fmtClv(dk)}</Box></>, true)
          : m.platforms[0]?.avgClv != null && cell("Your edge", fmtClv(m.platforms[0]), true)}
        {m.platforms.map(p => p.personality && cell(m.hasMultiplePlatforms ? `${p.provider === "DraftKings" ? "DK" : "UD"} personality` : "Personality", <Box component="span" sx={{ fontSize: 16 }}>{p.personality.primary}</Box>))}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, pt: 1, fontSize: 12, color: C.dim, fontWeight: 600 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: C.greenOnDark }} /> app.rosteros.com
      </Box>
    </Box>
  );
}

// ── main story ────────────────────────────────────────────────────

export default function WrappedStory({ metrics, onClose }: { metrics: WrappedMetrics; onClose: () => void }) {
  const reduced = useReducedMotion();
  const slides = metrics.slides;
  const [index, setIndex] = useState(0);
  const [autoOn] = useState(!reduced);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const isSummary = slides[index]?.type === "summary";

  const next = useCallback(() => setIndex(i => Math.min(i + 1, slides.length - 1)), [slides.length]);
  const prev = useCallback(() => setIndex(i => Math.max(i - 1, 0)), []);

  useEffect(() => { rootRef.current?.focus(); }, []);

  // autoplay
  useEffect(() => {
    if (!autoOn || reduced || isSummary) return;
    const id = setTimeout(next, SLIDE_MS);
    return () => clearTimeout(id);
  }, [index, autoOn, reduced, isSummary, next]);

  // keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      else if (e.key === "Escape") { onClose(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, onClose]);

  const exportCard = useCallback(async (share: boolean) => {
    if (!cardRef.current) return;
    try {
      if (document.fonts?.ready) await document.fonts.ready;
      const url = await toPng(cardRef.current, { backgroundColor: "#0d1f0b", pixelRatio: 2, cacheBust: true });
      if (share && (navigator as any).canShare) {
        const blob = await (await fetch(url)).blob();
        const file = new File([blob], "rosterOS-season-wrapped-2026.png", { type: "image/png" });
        if ((navigator as any).canShare({ files: [file] })) {
          await (navigator as any).share({ files: [file], title: "My rosterOS Season Wrapped", text: "My 2026 best ball draft season, wrapped." });
          return;
        }
      }
      const a = document.createElement("a");
      a.href = url; a.download = "rosterOS-season-wrapped-2026.png";
      document.body.appendChild(a); a.click(); a.remove();
      setToast("Saved your Wrapped card");
      setTimeout(() => setToast(null), 2400);
    } catch {
      setToast("Couldn't render the image — try again");
      setTimeout(() => setToast(null), 2400);
    }
  }, []);

  const slide = slides[index];

  return (
    <Box
      ref={rootRef}
      tabIndex={0}
      sx={{
        position: "fixed", inset: 0, zIndex: 2000, outline: "none",
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        color: C.text, background: "#08130a", overflow: "hidden", userSelect: "none",
      }}
    >
      {/* progress segments */}
      <Box sx={{ position: "absolute", top: 14, left: 14, right: 14, zIndex: 40, display: "flex", gap: "5px", height: "3px" }}>
        {slides.map((_, i) => (
          <Box key={i} sx={{ flex: 1, borderRadius: "2px", background: "rgba(255,255,255,.22)", overflow: "hidden" }}>
            <Box sx={{
              height: "100%", borderRadius: "2px", background: "#f4faf1",
              width: i < index ? "100%" : "0%",
              ...(i === index && autoOn && !reduced && !isSummary ? {
                animation: `wrapFill ${SLIDE_MS}ms linear forwards`,
                "@keyframes wrapFill": { from: { width: "0%" }, to: { width: "100%" } },
              } : {}),
            }} />
          </Box>
        ))}
      </Box>

      {/* close */}
      <Box component="button" aria-label="Close" onClick={onClose} sx={{
        position: "absolute", top: 24, right: 16, zIndex: 40, width: 30, height: 30, borderRadius: "50%",
        background: "rgba(0,0,0,.3)", border: `1px solid rgba(255,255,255,.14)`, color: "#eaf5e6",
        cursor: "pointer", display: "grid", placeItems: "center", fontSize: 16, lineHeight: 1,
      }}>×</Box>

      {/* slide */}
      <Box key={index} sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: "center", p: "62px 34px 48px", background: bgFor(slide.type) }}>
        {isSummary ? <SummaryCard m={metrics} cardRef={cardRef} /> : <SlideBody slide={slide} m={metrics} reduced={reduced} />}
      </Box>

      {/* tap zones */}
      <Box component="button" aria-label="Previous" onClick={prev} sx={{ position: "absolute", top: 0, bottom: 0, left: 0, width: "32%", zIndex: 30, background: "transparent", border: 0, cursor: "pointer" }} />
      <Box component="button" aria-label="Next" onClick={next} sx={{ position: "absolute", top: 0, bottom: 0, right: 0, width: "68%", zIndex: 30, background: "transparent", border: 0, cursor: "pointer" }} />

      {/* share actions on summary */}
      {isSummary && (
        <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 22, zIndex: 45, display: "flex", gap: 1.25, justifyContent: "center", px: "30px" }}>
          <Box component="button" onClick={() => exportCard(true)} sx={{ flex: 1, maxWidth: 180, fontSize: 14, fontWeight: 700, borderRadius: "12px", p: "13px 18px", cursor: "pointer", background: "rgba(255,255,255,.06)", color: "#eaf5e6", border: `1px solid ${C.hairline}` }}>Share</Box>
          <Box component="button" onClick={() => exportCard(false)} sx={{ flex: 1, maxWidth: 180, fontSize: 14, fontWeight: 700, borderRadius: "12px", p: "13px 18px", cursor: "pointer", background: "#eaf5e6", color: "#12240f", border: "1px solid transparent" }}>Save image</Box>
        </Box>
      )}

      {toast && (
        <Box sx={{ position: "absolute", left: "50%", bottom: 84, transform: "translateX(-50%)", zIndex: 50, background: "#12240f", color: "#eaf5e6", fontSize: 13, fontWeight: 600, px: 2.25, py: 1.25, borderRadius: "12px", border: `1px solid ${C.hairline}` }}>{toast}</Box>
      )}
    </Box>
  );
}
